import React, { useEffect, useMemo, useState } from "react";

const API_BASE = "https://node-api.mediitacademy.com/api";
// const API_BASE = "http://localhost:9090/api";

type Course = {
  id: number;
  title: string;
  category?: string;
  teacher?: string;
};

type Batch = {
  id: number;
  courseId: number;
  courseTitle: string;
  courseCategory?: string;
  name: string;
  teacher?: string | null;
  startDate: string;
  startTime: string;
  startAt?: string;
  days: string;
  seats: number;
  enrolledStudents: number;
  status: "Active" | "Upcoming" | "Draft" | "Closed" | "Inactive" | string;
  liveStatus?: string;
  description?: string | null;
};

type BatchStudent = {
  enrollmentId: number;
  studentName?: string | null;
  email: string;
  mobileNumber?: string | null;
  courseTitle?: string | null;
  batchName?: string | null;
  planName?: string | null;
  paymentStatus?: string | null;
  amountPaid?: number | string | null;
  transactionId?: string | null;
  enrolledAt?: string | null;
};

type BatchForm = {
  courseId: string;
  name: string;
  teacher: string;
  startDate: string;
  startTime: string;
  days: string;
  seats: string;
  status: string;
  description: string;
};

const emptyForm: BatchForm = {
  courseId: "",
  name: "",
  teacher: "",
  startDate: "",
  startTime: "08:00",
  days: "Mon - Sat",
  seats: "100",
  status: "Upcoming",
  description: "",
};

const formatDateTime = (date?: string | null, time?: string | null, startAt?: string | null) => {
  const raw = startAt || (date && time ? `${date}T${String(time).slice(0, 5)}` : date || "");
  if (!raw) return "-";

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return `${date || ""} ${time ? String(time).slice(0, 5) : ""}`.trim();
  }

  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (value?: number | string | null) => {
  if (value === undefined || value === null || value === "") return "-";
  const amount = Number(value);
  return Number.isNaN(amount) ? String(value) : `₹${amount.toLocaleString("en-IN")}`;
};

const getBatchType = (batch: Batch) => {
  const text = `${batch.courseCategory || ""} ${batch.courseTitle || ""}`.toLowerCase();
  if (text.includes("neet")) return "NEET";
  if (text.includes("jee")) return "JEE";
  if (text.includes("class 8")) return "Class 8";
  if (text.includes("class 7")) return "Class 7";
  return batch.courseCategory || "Other";
};

const AdminBatches: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [activeType, setActiveType] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState<Batch | null>(null);
  const [form, setForm] = useState<BatchForm>(emptyForm);
  const [studentsModal, setStudentsModal] = useState<{ batch: Batch; students: BatchStudent[] } | null>(null);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [teachers, setTeachers] = useState<any[]>([]);

  const fetchCourses = async () => {
    const response = await fetch(`${API_BASE}/courses`);
    const data = await response.json();
    setCourses(Array.isArray(data) ? data : []);
  };

  const fetchBatches = async () => {
    const response = await fetch(`${API_BASE}/batches`);
    const data = await response.json();
    setBatches(Array.isArray(data) ? data : []);
  };

  const fetchTeachers = async () => {
    const response = await fetch(`${API_BASE}/teachers`);
    const data = await response.json();
    setTeachers(Array.isArray(data) ? data : data?.teachers || []);
  };

  const loadPageData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchCourses(), fetchBatches(), fetchTeachers()]);
    } catch (error) {
      console.error("Error loading batches:", error);
      alert("Batch data load nahi ho paya. Backend route /api/batches check karo.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPageData();
  }, []);

  const batchTypes = useMemo(() => {
    const values = new Set<string>(["All", "NEET", "JEE", "Class 8", "Class 7"]);
    batches.forEach((batch) => values.add(getBatchType(batch)));
    return Array.from(values).filter(Boolean);
  }, [batches]);

  const filteredBatches = useMemo(() => {
    const text = search.toLowerCase().trim();

    return batches.filter((batch) => {
      const type = getBatchType(batch);
      const matchType = activeType === "All" || type.toLowerCase() === activeType.toLowerCase();
      const searchable = `${batch.name} ${batch.courseTitle} ${batch.courseCategory || ""} ${batch.teacher || ""} ${batch.days} ${batch.status}`.toLowerCase();
      const matchSearch = text === "" || searchable.includes(text);
      return matchType && matchSearch;
    });
  }, [batches, activeType, search]);

  const stats = useMemo(() => {
    const active = batches.filter((batch) => ["Active", "Upcoming"].includes(batch.status)).length;
    const totalSeats = batches.reduce((sum, batch) => sum + Number(batch.seats || 0), 0);
    const occupied = batches.reduce((sum, batch) => sum + Number(batch.enrolledStudents || 0), 0);
    const occupancy = totalSeats ? Math.round((occupied / totalSeats) * 100) : 0;

    return {
      total: batches.length,
      active,
      students: occupied,
      occupancy,
    };
  }, [batches]);

  const openCreateModal = () => {
    setEditingBatch(null);
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const openEditModal = (batch: Batch) => {
    setEditingBatch(batch);
    setForm({
      courseId: String(batch.courseId),
      name: batch.name || "",
      teacher: batch.teacher || "",
      startDate: batch.startDate ? String(batch.startDate).slice(0, 10) : "",
      startTime: batch.startTime ? String(batch.startTime).slice(0, 5) : "08:00",
      days: batch.days || "Mon - Sat",
      seats: String(batch.seats || 100),
      status: batch.status || "Upcoming",
      description: batch.description || "",
    });
    setIsModalOpen(true);
  };

  const handleCourseChange = (courseId: string) => {
    const course = courses.find((item) => String(item.id) === courseId);
    setForm((previous) => ({
      ...previous,
      courseId,
      teacher: previous.teacher || course?.teacher || "",
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.courseId || !form.startDate || !form.startTime) {
      alert("Course, start date aur start time required hai.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        courseId: Number(form.courseId),
        name: form.name.trim(),
        teacher: form.teacher.trim(),
        startDate: form.startDate,
        startTime: form.startTime,
        days: form.days,
        seats: Number(form.seats || 100),
        status: form.status,
        description: form.description.trim(),
      };

      const response = await fetch(editingBatch ? `${API_BASE}/batches/${editingBatch.id}` : `${API_BASE}/batches`, {
        method: editingBatch ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || data?.message || "Batch save failed.");
      }

      setIsModalOpen(false);
      setForm(emptyForm);
      setEditingBatch(null);
      await fetchBatches();
      alert(editingBatch ? "Batch updated successfully!" : "Batch created successfully!");
    } catch (error) {
      alert(error instanceof Error ? error.message : "Batch save nahi hua.");
    } finally {
      setSaving(false);
    }
  };

  const closeBatch = async (batch: Batch) => {
    if (!window.confirm(`Is batch ko close/delete karna hai?\n${batch.name}`)) return;

    try {
      const response = await fetch(`${API_BASE}/batches/${batch.id}`, { method: "DELETE" });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.error || "Batch delete/close failed.");
      await fetchBatches();
      alert(data?.message || "Batch updated.");
    } catch (error) {
      alert(error instanceof Error ? error.message : "Batch delete/close nahi hua.");
    }
  };

  const viewStudents = async (batch: Batch) => {
    setStudentsLoading(true);
    setStudentsModal({ batch, students: [] });

    try {
      const response = await fetch(`${API_BASE}/batches/${batch.id}/students`);
      const data = await response.json();
      setStudentsModal({ batch, students: Array.isArray(data) ? data : [] });
    } catch (error) {
      console.error("Batch students error:", error);
      alert("Students load nahi ho paye.");
    } finally {
      setStudentsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="relative overflow-hidden rounded-[30px] bg-slate-950 p-6 text-white shadow-xl shadow-slate-300/40 sm:p-8">
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-blue-500/30 blur-3xl" />
        <div className="absolute -bottom-24 left-1/2 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />

        <div className="relative flex flex-col justify-between gap-6 lg:flex-row lg:items-center">
          <div>
            <div className="mb-4 inline-flex rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-wide text-cyan-100">
              Dynamic Batch Management
            </div>
            <h1 className="max-w-3xl text-3xl font-black tracking-tight sm:text-4xl">
              Course-wise batches, start date, time and enrollments
            </h1>
            <p className="mt-3 max-w-2xl text-sm font-semibold leading-6 text-slate-300">
              Pehle course select karo, phir batch start date/time set karo. Student purchase ke time sirf upcoming batches select kar payega.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button onClick={loadPageData} className="rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-black text-white hover:bg-white/15">
              Refresh
            </button>
            <button onClick={openCreateModal} className="rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-950 hover:bg-cyan-50">
              + Create Batch
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Total Batches" value={stats.total.toString()} note="Database se live" />
        <StatCard title="Open Batches" value={stats.active.toString()} note="Active / Upcoming" />
        <StatCard title="Total Enrolled" value={stats.students.toLocaleString("en-IN")} note="All batch students" />
        <StatCard title="Seat Occupancy" value={`${stats.occupancy}%`} note="Booked seats" />
      </section>

      <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap gap-2">
            {batchTypes.map((type) => (
              <button
                key={type}
                onClick={() => setActiveType(type)}
                className={`rounded-2xl px-4 py-2 text-sm font-black transition ${
                  activeType === type ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {type}
              </button>
            ))}
          </div>

          <div className="flex h-12 w-full items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 xl:max-w-md">
            <svg viewBox="0 0 24 24" className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" />
            </svg>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search batch, course, teacher..."
              className="w-full bg-transparent text-sm font-semibold outline-none placeholder:text-slate-400"
            />
          </div>
        </div>
      </section>

      {loading ? (
        <div className="rounded-[28px] border border-slate-200 bg-white p-10 text-center text-sm font-black text-slate-500">
          Loading batches...
        </div>
      ) : filteredBatches.length === 0 ? (
        <div className="rounded-[28px] border border-dashed border-slate-300 bg-white p-10 text-center">
          <h2 className="text-xl font-black text-slate-950">Abhi koi batch nahi mila</h2>
          <p className="mt-2 text-sm font-bold text-slate-500">+ Create Batch par click karke first batch banao.</p>
        </div>
      ) : (
        <section className="grid gap-6 xl:grid-cols-[1fr_360px]">
          <div className="grid gap-5 lg:grid-cols-2">
            {filteredBatches.map((batch) => {
              const type = getBatchType(batch);
              const seatPercent = batch.seats ? Math.min(100, Math.round((Number(batch.enrolledStudents || 0) / Number(batch.seats || 1)) * 100)) : 0;

              return (
                <div key={batch.id} className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex gap-4">
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-lg font-black text-white shadow-lg">
                        {type.slice(0, 1)}
                      </div>
                      <div>
                        <div className="flex flex-wrap gap-2">
                          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-600">{type}</span>
                          <StatusBadge status={batch.liveStatus || batch.status} />
                        </div>
                        <h2 className="mt-3 text-lg font-black leading-6 text-slate-950">{batch.name}</h2>
                        <p className="mt-1 text-sm font-semibold text-slate-500">{batch.courseTitle}</p>
                      </div>
                    </div>

                    <button onClick={() => openEditModal(batch)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-black text-slate-600 hover:bg-slate-50">
                      Edit
                    </button>
                  </div>

                  <div className="mt-6 grid gap-3 sm:grid-cols-2">
                    <InfoBox label="Teacher" value={batch.teacher || "-"} />
                    <InfoBox label="Timing" value={`${batch.days}, ${String(batch.startTime || "").slice(0, 5)}`} />
                    <InfoBox label="Start" value={formatDateTime(batch.startDate, batch.startTime, batch.startAt)} />
                    <InfoBox label="Students" value={`${batch.enrolledStudents || 0}/${batch.seats}`} />
                  </div>

                  <div className="mt-5">
                    <div className="mb-2 flex justify-between text-xs font-black text-slate-500">
                      <span>Seat Occupancy</span>
                      <span>{seatPercent}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100">
                      <div className="h-2 rounded-full bg-blue-600" style={{ width: `${seatPercent}%` }} />
                    </div>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-3">
                    <button onClick={() => viewStudents(batch)} className="rounded-2xl bg-slate-950 px-4 py-2.5 text-xs font-black text-white hover:bg-blue-600">
                      View Students
                    </button>
                    <button onClick={() => openEditModal(batch)} className="rounded-2xl bg-slate-100 px-4 py-2.5 text-xs font-black text-slate-700 hover:bg-slate-200">
                      Edit Batch
                    </button>
                    <button onClick={() => closeBatch(batch)} className="rounded-2xl bg-red-50 px-4 py-2.5 text-xs font-black text-red-700 hover:bg-red-100">
                      Close/Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <aside className="space-y-6">
            <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-xl font-black text-slate-950">Upcoming Batches</h2>
              <div className="mt-5 space-y-4">
                {batches
                  .filter((batch) => (batch.liveStatus || batch.status) === "Upcoming")
                  .slice(0, 5)
                  .map((batch) => (
                    <div key={batch.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <h3 className="text-sm font-black leading-5 text-slate-950">{batch.name}</h3>
                      <p className="mt-1 text-xs font-bold text-slate-500">{batch.courseTitle}</p>
                      <p className="mt-3 text-xs font-black text-blue-700">{formatDateTime(batch.startDate, batch.startTime, batch.startAt)}</p>
                    </div>
                  ))}

                {batches.filter((batch) => (batch.liveStatus || batch.status) === "Upcoming").length === 0 && (
                  <p className="text-sm font-bold text-slate-500">No upcoming batch.</p>
                )}
              </div>
            </div>

            <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-xl font-black text-slate-950">Flow</h2>
              <div className="mt-5 space-y-3 text-sm font-bold text-slate-600">
                <p>1. Course banao.</p>
                <p>2. Isi page se us course ka batch banao.</p>
                <p>3. Purchase page par student upcoming batch select karega.</p>
                <p>4. Enrollment me course + batch dono save honge.</p>
              </div>
            </div>
          </aside>
        </section>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
          <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-[34px] border border-slate-200 bg-white shadow-2xl">
            <div className="flex shrink-0 items-center justify-between border-b border-slate-100 bg-slate-50 p-6 lg:p-8">
              <div>
                <h2 className="text-2xl font-black text-slate-950">{editingBatch ? "Edit Batch" : "Create New Batch"}</h2>
                <p className="mt-1 text-sm font-bold text-slate-500">Course select karke batch start date/time set karo.</p>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-200 font-black text-slate-600 hover:bg-red-100 hover:text-red-600">
                X
              </button>
            </div>

            <form id="batchForm" onSubmit={handleSubmit} className="flex-1 space-y-6 overflow-y-auto p-6 lg:p-8">
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">Select Course</label>
                  <select
                    required
                    value={form.courseId}
                    onChange={(event) => handleCourseChange(event.target.value)}
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
                  >
                    <option value="">Choose course</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.title} {course.category ? `(${course.category})` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">Batch Name</label>
                  <input
                    value={form.name}
                    onChange={(event) => setForm({ ...form, name: event.target.value })}
                    placeholder="e.g. NEET 2026 Morning Batch"
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
                  />
                  <p className="mt-1 text-[11px] font-bold text-slate-400">Blank chhodoge to backend auto name bana dega.</p>
                </div>
              </div>

              <div className="grid gap-5 md:grid-cols-4">
                <div>
                  <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">Start Date</label>
                  <input
                    required
                    type="date"
                    value={form.startDate}
                    onChange={(event) => setForm({ ...form, startDate: event.target.value })}
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">Start Time</label>
                  <input
                    required
                    type="time"
                    value={form.startTime}
                    onChange={(event) => setForm({ ...form, startTime: event.target.value })}
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">Days</label>
                  <input
                    value={form.days}
                    onChange={(event) => setForm({ ...form, days: event.target.value })}
                    placeholder="Mon - Sat"
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">Seats</label>
                  <input
                    required
                    type="number"
                    min="1"
                    value={form.seats}
                    onChange={(event) => setForm({ ...form, seats: event.target.value })}
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
                  />
                </div>
              </div>

              <div className="grid gap-5 md:grid-cols-2">
            <div>
    <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">Assign Teacher</label>
    <select
      value={form.teacher}
      onChange={(event) => setForm({ ...form, teacher: event.target.value })}
      className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
    >
      <option value="">-- Select Teacher --</option>
      {teachers.map((t: any) => (
        <option key={t.id} value={t.name}>{t.name} ({t.subject || 'All'})</option>
      ))}
    </select>
  </div>

                <div>
                  <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">Status</label>
                  <select
                    value={form.status}
                    onChange={(event) => setForm({ ...form, status: event.target.value })}
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
                  >
                    <option value="Upcoming">Upcoming</option>
                    <option value="Active">Active</option>
                    <option value="Draft">Draft</option>
                    <option value="Closed">Closed</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">Description</label>
                <textarea
                  value={form.description}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                  rows={3}
                  placeholder="Batch notes / instructions"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
                />
              </div>
            </form>

            <div className="flex shrink-0 justify-end gap-4 border-t border-slate-100 bg-white p-6">
              <button type="button" onClick={() => setIsModalOpen(false)} className="rounded-2xl px-6 py-3 text-sm font-black text-slate-600 hover:bg-slate-100">
                Cancel
              </button>
              <button disabled={saving} form="batchForm" type="submit" className="rounded-2xl bg-[#101827] px-8 py-3 text-sm font-black text-white shadow-lg hover:bg-blue-600 disabled:bg-slate-300">
                {saving ? "Saving..." : editingBatch ? "Update Batch" : "Create Batch"}
              </button>
            </div>
          </div>
        </div>
      )}

      {studentsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-[34px] border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 p-6">
              <div>
                <h2 className="text-2xl font-black text-slate-950">Batch Students</h2>
                <p className="mt-1 text-sm font-bold text-slate-500">{studentsModal.batch.name}</p>
              </div>
              <button onClick={() => setStudentsModal(null)} className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-200 font-black text-slate-600 hover:bg-red-100 hover:text-red-600">
                X
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto p-6">
              {studentsLoading ? (
                <p className="text-center text-sm font-black text-slate-500">Loading students...</p>
              ) : studentsModal.students.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm font-bold text-slate-500">
                  Is batch me abhi koi student enrolled nahi hai.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-slate-100">
                  <table className="w-full min-w-[900px] text-left">
                    <thead>
                      <tr className="bg-slate-50 text-xs font-black uppercase tracking-wide text-slate-400">
                        <th className="px-4 py-3">Student</th>
                        <th className="px-4 py-3">Course / Batch</th>
                        <th className="px-4 py-3">Plan</th>
                        <th className="px-4 py-3">Amount</th>
                        <th className="px-4 py-3">Payment</th>
                        <th className="px-4 py-3">Enrolled At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {studentsModal.students.map((student) => (
                        <tr key={student.enrollmentId} className="border-t border-slate-100">
                          <td className="px-4 py-3">
                            <p className="text-sm font-black text-slate-900">{student.studentName || student.email}</p>
                            <p className="text-xs font-bold text-slate-500">{student.email}</p>
                            <p className="text-xs font-bold text-slate-400">{student.mobileNumber || "-"}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-black text-slate-900">{student.courseTitle || "-"}</p>
                            <p className="text-xs font-bold text-slate-500">{student.batchName || "-"}</p>
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-slate-600">{student.planName || "-"}</td>
                          <td className="px-4 py-3 text-sm font-black text-slate-900">{formatAmount(student.amountPaid)}</td>
                          <td className="px-4 py-3"><PaymentBadge status={student.paymentStatus || "PENDING"} /></td>
                          <td className="px-4 py-3 text-xs font-bold text-slate-500">{formatDateTime(student.enrolledAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ title, value, note }: { title: string; value: string; note: string }) => (
  <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
    <p className="text-sm font-bold text-slate-500">{title}</p>
    <h2 className="mt-2 text-3xl font-black text-slate-950">{value}</h2>
    <p className="mt-1 text-xs font-bold text-emerald-600">{note}</p>
  </div>
);

const InfoBox = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-2xl bg-slate-50 p-3">
    <p className="text-xs font-bold text-slate-400">{label}</p>
    <p className="mt-1 text-sm font-black text-slate-900">{value}</p>
  </div>
);

const StatusBadge = ({ status }: { status: string }) => {
  const normalized = status || "Upcoming";
  const className =
    normalized === "Started"
      ? "bg-slate-100 text-slate-700"
      : normalized === "Active" || normalized === "Upcoming"
      ? "bg-emerald-50 text-emerald-700"
      : normalized === "Draft"
      ? "bg-orange-50 text-orange-700"
      : "bg-red-50 text-red-700";

  return <span className={`rounded-full px-3 py-1 text-xs font-black ${className}`}>{normalized}</span>;
};

const PaymentBadge = ({ status }: { status: string }) => {
  const normalized = status.toUpperCase();
  const className =
    normalized === "PAID" || normalized === "APPROVED" || normalized === "SUCCESS"
      ? "bg-emerald-50 text-emerald-700"
      : normalized === "PENDING"
      ? "bg-orange-50 text-orange-700"
      : "bg-red-50 text-red-700";

  return <span className={`rounded-full px-3 py-1 text-xs font-black ${className}`}>{status}</span>;
};

export default AdminBatches;
